import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useQuery, useQueryClient } from 'react-query'
import Swal from 'sweetalert2'
import { axiosClientAuth } from '../../../../../app/utils'
import { KTIcon } from '../../../../helpers'

const ModalEditUser = ({ show, setShow, currData }) => {
  const queryClient = useQueryClient()
  const [submitting, setSubmitting] = useState(false)
  const [input, setInput] = useState({ name: '', email: '', phone: '', groups: '' })
  const [originalRoleId, setOriginalRoleId] = useState('')

  const { data: userDetail, isLoading: loadingUser } = useQuery(
    ['user-detail', currData?.uuid],
    () =>
      axiosClientAuth
        .get(`/auth/users/${currData.uuid}/`)
        .then((res) => res.data?.data),
    { enabled: !!currData?.uuid }
  )

  useEffect(() => {
    if (userDetail) {
      const roleId = String(userDetail.role?.id ?? '')
      setInput({
        name: userDetail.name ?? '',
        email: userDetail.email ?? '',
        phone: userDetail.phone ?? '',
        groups: roleId,
      })
      setOriginalRoleId(roleId)
    }
  }, [userDetail])

  const { data: roles = [] } = useQuery(
    ['roles-for-edit-user'],
    () =>
      axiosClientAuth
        .get('/auth/roles/', { params: { page: 1, page_size: 100 } })
        .then((res) => res.data?.data?.results ?? []),
    { enabled: show }
  )

  const onChangeHandler = (e) => {
    setInput((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      await axiosClientAuth.patch(`/auth/users/${currData.uuid}/update/`, {
        name: input.name,
        email: input.email,
        phone: input.phone,
      })
      if (input.groups && input.groups !== originalRoleId) {
        await axiosClientAuth.post(`/auth/users/${currData.uuid}/assign-role/`, {
          role_id: parseInt(input.groups),
        })
      }
      await Swal.fire({
        titleText: 'Success!',
        text: 'User updated successfully',
        icon: 'success',
        confirmButtonText: 'Ok',
      })
      queryClient.invalidateQueries(['users'])
      setShow(false)
    } catch (err: any) {
      Swal.fire({
        titleText: 'Error!',
        text: err?.response?.data?.message || 'Failed to update user',
        icon: 'error',
        confirmButtonText: 'Ok',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal className='modal fade modal-lg' show={show}>
      <div className='modal-header'>
        <h2 className='fw-bold'>Edit User</h2>
        <div className='d-flex justify-content-end'>
          <button
            className='ms-auto btn btn-sm btn-icon btn-active-color-primary'
            onClick={() => setShow(false)}
          >
            <KTIcon iconName='cross' className='text-gray fs-1' />
          </button>
        </div>
      </div>
      <div className='modal-body scroll-y mx-5 mx-xl-15 my-7'>
        {loadingUser ? (
          <div className='text-center py-10'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        ) : (
          <form className='form' onSubmit={onSubmit}>
            <div
              className='d-flex flex-column scroll-y me-n7 pe-7'
              data-kt-scroll='true'
              data-kt-scroll-activate='{default: false, lg: true}'
              data-kt-scroll-max-height='auto'
              data-kt-scroll-dependencies='#kt_modal_add_user_header'
              data-kt-scroll-wrappers='#kt_modal_add_user_scroll'
              data-kt-scroll-offset='300px'
            >
              <div className='fv-row mb-7'>
                <label className='required fw-semibold fs-6 mb-2'>Full Name</label>
                <input
                  type='text'
                  name='name'
                  required
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  placeholder='Full name'
                  value={input.name}
                  onChange={onChangeHandler}
                />
              </div>
              <div className='fv-row mb-7'>
                <label className='required fw-semibold fs-6 mb-2'>Email ID</label>
                <input
                  type='email'
                  name='email'
                  required
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  placeholder='example@domain.com'
                  value={input.email}
                  onChange={onChangeHandler}
                />
              </div>
              <div className='fv-row mb-7'>
                <label className='fw-semibold fs-6 mb-2'>Phone</label>
                <input
                  type='text'
                  name='phone'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  placeholder='1234567890'
                  value={input.phone}
                  onChange={onChangeHandler}
                />
              </div>
              <div className='mb-7'>
                <label className='required fw-semibold fs-6 mb-5'>Role</label>
                {roles.map((role: any) => (
                  <React.Fragment key={role.id}>
                    <div className='d-flex fv-row'>
                      <div className='form-check form-check-custom form-check-solid'>
                        <input
                          className='form-check-input me-3'
                          name='groups'
                          type='radio'
                          value={String(role.id)}
                          checked={input.groups === String(role.id)}
                          onChange={onChangeHandler}
                        />
                        <label className='form-check-label'>
                          <div className='fw-bold text-gray-800'>{role.name}</div>
                          <div className='text-gray-600'>{role.description}</div>
                        </label>
                      </div>
                    </div>
                    <div className='separator separator-dashed my-5' />
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className='text-center pt-15'>
              <button
                type='button'
                className='btn btn-light me-3'
                onClick={() => setShow(false)}
              >
                Discard
              </button>
              <button type='submit' className='btn btn-primary' disabled={submitting}>
                {submitting ? (
                  <span className='indicator-progress d-flex align-items-center'>
                    Please wait...
                    <span className='spinner-border spinner-border-sm align-middle ms-2' />
                  </span>
                ) : (
                  <span className='indicator-label'>Submit</span>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  )
}

export default ModalEditUser
